/* eslint-disable max-len */
/* eslint-disable react/prop-types */
/* eslint-disable object-curly-newline */
import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../../../contexts/User';
import { sendRequest } from '../../../api/shootAPI';
import API from '../../../api/endpoints';
import '../settings.css';
import DataTable from '../../../components/DataTable';
import StatusPill from '../../../components/StatusPill';

const PAGE_SIZE = 10;
const dash = <span className="text-ink-500">—</span>;
const STATUS_TONE = { success: 'success', failed: 'error', pending: 'warning' };
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s);

const fmtDate = (d) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

function History() {
  const { activeWorkspace } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const handleDownloadInvoice = async (transactionId) => {
    try {
      const response = await sendRequest({
        method: 'post',
        url: API.WORKSPACE.downloadInvoice(activeWorkspace.id, transactionId),
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${transactionId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error downloading invoice:', error);
    }
  };

  const getTransactions = async (nextPage = 1) => {
    setIsLoading(true);
    try {
      const res = await sendRequest({
        method: 'get',
        url: `${API.WORKSPACE.getTransactions(activeWorkspace.id)}?page=${nextPage}&limit=${PAGE_SIZE}`,
      });
      setTransactions(res.data?.transactions ?? []);
      setTotal(res.data?.pagination?.total ?? (res.data?.transactions?.length ?? 0));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching transactions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { getTransactions(page); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [page]);

  const columns = [
    {
      key: 'amount',
      label: 'Amount',
      width: 120,
      render: (t) => {
        const hasDiscount = t.couponId && t.discountAmount;
        const original = hasDiscount ? t.originalAmount : t.amount;
        return hasDiscount ? (
          <div>
            <div className="text-ink-500 line-through" style={{ fontSize: 12 }}>{`$${parseFloat(original).toFixed(2)}`}</div>
            <div className="font-semibold" style={{ color: '#53A551' }}>{`$${parseFloat(t.amount).toFixed(2)}`}</div>
          </div>
        ) : <span className="text-ink-900 tabular-nums">{`$${parseFloat(t.amount).toFixed(2)}`}</span>;
      },
    },
    { key: 'plan', label: 'Plan', width: 130, render: (t) => <span className="text-ink-900">{t.planName || dash}</span> },
    { key: 'users', label: 'Users', width: 90, render: (t) => <span className="text-ink-700 tabular-nums">{t.numberOfUsers || dash}</span> },
    {
      key: 'discount',
      label: 'Discount',
      width: 130,
      render: (t) => ((t.couponId && t.discountAmount) ? (
        <div style={{ color: '#53A551' }}>
          <div className="font-semibold" style={{ fontSize: 12 }}>{t.coupon?.code || 'COUPON'}</div>
          <div style={{ fontSize: 11 }}>{`-$${parseFloat(t.discountAmount).toFixed(2)}`}</div>
        </div>
      ) : dash),
    },
    { key: 'paymentMethod', label: 'Payment Method', width: 150, render: (t) => <span className="text-ink-700">{t.paymentMethod || dash}</span> },
    { key: 'customer', label: 'Customer', width: 200, flex: true, render: (t) => <span className="text-ink-700 truncate block" title={t.customerEmail || t.customerName}>{t.customerEmail || t.customerName || dash}</span> },
    { key: 'date', label: 'Date', width: 130, render: (t) => <span className="text-ink-700 whitespace-nowrap">{fmtDate(t.createdAt)}</span> },
    { key: 'status', label: 'Status', width: 120, render: (t) => <StatusPill tone={STATUS_TONE[t.status] || 'neutral'}>{cap(t.status)}</StatusPill> },
    {
      key: 'action',
      label: 'Action',
      width: 160,
      render: (t) => (t.status === 'success' ? (
        <button type="button" onClick={() => handleDownloadInvoice(t.id)} className="inline-flex items-center justify-center px-3 py-1.5 rounded-[8px] text-[11px] font-semibold uppercase tracking-wider text-ink-900 bg-white border border-rule hover:border-brand-500 hover:text-brand-700 transition whitespace-nowrap" style={{ borderStyle: 'solid', letterSpacing: '0.05em' }}>
          Download Invoice
        </button>
      ) : dash),
    },
  ];

  return (
    <>
      <div className="p-4">
        <h4 className="text-base font-semibold text-ink-900 mb-0.5">Transaction History</h4>
        <p className="text-sm text-ink-500">View all payment transactions and download invoices for successful payments</p>
      </div>
      <DataTable
        bare
        columns={columns}
        rows={transactions}
        getRowKey={(t, i) => t.id ?? i}
        loading={isLoading}
        emptyText="No transactions yet."
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
        minWidth={1200}
      />
    </>
  );
}

export default History;
